import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, FormControl, InputLabel, Select, MenuItem, useTheme, useMediaQuery, Alert } from '@mui/material';
import { Autocomplete } from '@mui/material';
import DropdownProductCode from './Challan_Stepper/components/DropdownProductCode';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const MultiLevelBOMForm = ({ connectionString, loggedInUser, companyData }) => {
    const { user, selectedCompany, loading } = useAuth();
    const userId = user?.UserId || null;
    console.log(user, selectedCompany)
    const companyId = selectedCompany?.CompId || null;
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('md'));
    const state = loggedInUser?.details?.['State'] || '';
    const compState = selectedCompany?.State || '';
    const [type, setType] = useState('Finished Goods');
    const [bomProductCode, setBomProductCode] = useState('');
    const [bomDescription, setBomDescription] = useState('');
    const [error, setError] = useState(null);
    const [productOptions, setProductOptions] = useState([]); // New state for Autocomplete
    const [totals, setTotals] = useState({ basic: 0, discount: 0, cgst: 0, sgst: 0, igst: 0, roundOff: 0, total: 0 });
    const [topLevelProduct, setTopLevelProduct] = useState({
        Id: '',
        itemCode: '',
        description: '',
        Unit: '',
        GST: '0.00',
        saleRate: 0,
        saleDiscount: 0,
        TaxId: null
    });

    const [levels, setLevels] = useState([
        {
            id: 0,
            current: {
                Id: '',
                productCode: '',
                description: '',
                UnitId: '',
                unit: '',
                qty: 1,
                rate: 0,
                disc: 0,
                tax: 0,
                batchNo: '',
                expDate: '',
                serialNo: '',
                remark: '',
                CGST: '0.00',
                SGST: '0.00',
                IGST: '0.00',
                UTGST: '0.00',
                GSTCess: '0.00',
                TaxId: null,
                challanDtsID: null,
                challanID: null,
                soId: null,
                soDtsID: null
            },
            rows: [],
            nextSrNo: 1,
            errors: {},
            editIndex: null,
            unitOptions: [],
            allProducts: []
        }
    ]);

    useEffect(() => {
        const fetchUnits = async () => {
            try {
                const unitsRes = await axios.get(`${connectionString}/api/unit/retrive`);
                const units = unitsRes.data || [];
                setLevels(prev => prev.map(l => ({ ...l, unitOptions: units })));
            } catch (e) {
                console.error('Error fetching units:', e);
                setError('Failed to load units');
            }
        };
        fetchUnits();
    }, [connectionString]);

    const mapProductData = (product) => ({
        Id: product.Id,
        itemCode: product.itemCode,
        description: product.description,
        Unit: product.Unit || 'N/A',
        GST: product.TaxGST || '0.00',
        saleRate: product.saleRate || 0,
        saleDiscount: product.saleDiscount || 0,
        TaxId: product.TaxId || null
    });

    const fetchProducts = async (selectedType, itemCode) => {
        if (!selectedType || !itemCode) {
            setBomDescription('');
            setProductOptions([]); // Update productOptions
            setError(null);
            return;
        }
        try {
            const res = await axios.get(`${connectionString}/api/product/searchByType`, { params: { Type: selectedType, itemCode } });
            const products = Array.isArray(res.data) ? res.data.map(mapProductData) : [];
            setProductOptions(products); // Update productOptions
            const product = products.length > 0 ? products[0] : {};
            setBomDescription(product.description || '');
            setError(null);
        } catch (e) {
            console.error('Error fetching products:', e);
            setBomDescription('');
            setProductOptions([]); // Update productOptions
            setError('Failed to fetch products');
        }
    };

    const loadAllProducts = async () => {
        try {
            if (!type) {
                console.warn('Type is required to load products.');
                setError('Please select a product type');
                return;
            }
            const res = await axios.get(`${connectionString}/api/product/search-Type`, { params: { Type: type } });
            const products = Array.isArray(res.data) ? res.data.map(mapProductData) : [];
            setLevels(prev => prev.map(l => ({ ...l, allProducts: products })));
            setError(null);
        } catch (e) {
            console.error('Error loading products:', e);
            setLevels(prev => prev.map(l => ({ ...l, allProducts: [] })));
            setError('Failed to load products');
        }
    };

    const handleSelect = (levelId, product) => {
        const level = levels.find(l => l.id === levelId);
        if (!level) return;
        const matchedUnit = level.unitOptions.find(opt => {
            if (product.UnitId && opt.UnitId === product.UnitId) return true;
            if (product.Unit && opt.unit?.toLowerCase() === product.Unit.toLowerCase()) return true;
            return false;
        });
        const qty = 1;
        const rate = Number(product.saleRate) || 0;
        const disc = Number(product.saleDiscount) || 0;
        const tax = parseFloat(product.GST) || 0;
        setLevels(prev => prev.map(l => l.id === levelId ? {
            ...l,
            current: {
                Id: product.Id?.toString() || '',
                productCode: product.itemCode || '',
                description: product.description || '',
                UnitId: matchedUnit ? String(matchedUnit.UnitId) : '',
                unit: matchedUnit ? matchedUnit.unit : product.Unit || '',
                qty,
                rate,
                disc,
                tax,
                batchNo: '',
                expDate: '',
                serialNo: '',
                remark: '',
                CGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
                SGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
                IGST: state?.toLowerCase() !== compState?.toLowerCase() ? tax.toFixed(2) : '0.00',
                UTGST: '0.00',
                GSTCess: '0.00',
                TaxId: product.TaxId || null,
                challanDtsID: null,
                challanID: null,
                soId: null,
                soDtsID: null
            },
            editIndex: null,
            errors: {}
        } : l));
    };

    const handleBomProductSelect = (_, newProduct) => {
        if (newProduct) {
            setBomProductCode(newProduct.itemCode);
            setBomDescription(newProduct.description || '');
            setTopLevelProduct({
                Id: newProduct.Id?.toString() || '',
                itemCode: newProduct.itemCode || '',
                description: newProduct.description || '',
                Unit: newProduct.Unit || '',
                GST: newProduct.GST || '0.00',
                saleRate: Number(newProduct.saleRate) || 0,
                saleDiscount: Number(newProduct.saleDiscount) || 0,
                TaxId: newProduct.TaxId || null
            });
        } else {
            setBomProductCode('');
            setBomDescription('');
            setProductOptions([]);
            setTopLevelProduct({
                Id: '',
                itemCode: '',
                description: '',
                Unit: '',
                GST: '0.00',
                saleRate: 0,
                saleDiscount: 0,
                TaxId: null
            });
        }
    };

    const handleChange = (levelId, field) => (e) => {
        const value = e?.target ? e.target.value : e;
        setLevels(prev => prev.map(l => l.id === levelId ? { ...l, current: { ...l.current, [field]: value } } : l));
    };

    const addProduct = (levelId) => {
        const level = levels.find(l => l.id === levelId);
        if (!level) return;
        const current = level.current;
        const errs = {};
        const messages = new Set();
        const requiredFields = ['productCode', 'description', 'UnitId', 'qty', 'rate', 'tax'];
        requiredFields.forEach(field => {
            if (!current[field] && current[field] !== 0) {
                errs[field] = 'Required';
                messages.add('All fields are required.');
            }
        });
        if (!current.qty || current.qty < 1) {
            errs.qty = 'Must be > 0';
            messages.add('Qty must be greater than 0');
        }
        if (current.rate < 0) {
            errs.rate = 'Cannot be negative';
            messages.add('Rate cannot be negative');
        }
        if (messages.size > 0) {
            setLevels(prev => prev.map(l => l.id === levelId ? { ...l, errors: errs } : l));
            return;
        }
        const qty = Number(current.qty) || 0;
        const rate = Number(current.rate) || 0;
        const tax = Number(current.tax) || 0;
        const row = {
            ...current,
            productId: current.Id,
            qty,
            rate,
            tax,
            remark: current.remark || '',
            CGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            SGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            IGST: state?.toLowerCase() !== compState?.toLowerCase() ? tax.toFixed(2) : '0.00',
            UTGST: current.UTGST,
            GSTCess: current.GSTCess,
            TaxId: current.TaxId,
            srNo: level.editIndex != null ? level.rows[level.editIndex].srNo : level.nextSrNo,
            challanDtsID: level.editIndex != null ? level.rows[level.editIndex].challanDtsID : null,
            challanID: level.editIndex != null ? level.rows[level.editIndex].challanID : null,
            soId: level.editIndex != null ? level.rows[level.editIndex].soId : null,
            soDtsID: level.editIndex != null ? level.rows[level.editIndex].soDtsID : null
        };
        const existingRowIndex = level.rows.findIndex((r, i) => r.productCode === current.productCode && i !== level.editIndex);
        setLevels(prev => prev.map(l => {
            if (l.id !== levelId) return l;
            let newRows = [...l.rows];
            let newNextSr = l.nextSrNo;
            if (existingRowIndex !== -1 && l.editIndex == null) {
                newRows = newRows.map((r, i) => {
                    if (i === existingRowIndex) {
                        const newQty = Number(r.qty) + qty;
                        return {
                            ...r,
                            qty: newQty,
                            rate,
                            tax,
                            remark: current.remark || r.remark,
                            CGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
                            SGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
                            IGST: state?.toLowerCase() !== compState?.toLowerCase() ? tax.toFixed(2) : '0.00',
                            UTGST: current.UTGST,
                            GSTCess: current.GSTCess,
                            TaxId: current.TaxId,
                            challanDtsID: r.challanDtsID,
                            challanID: r.challanID,
                            soId: r.soId,
                            soDtsID: r.soDtsID
                        };
                    }
                    return r;
                });
            } else {
                if (l.editIndex != null) {
                    newRows = newRows.map((r, i) => (i === l.editIndex ? row : r));
                } else {
                    newRows = [row, ...newRows];
                    newNextSr = newNextSr + 1;
                }
            }
            const resetCurrent = {
                Id: '',
                productCode: '',
                description: '',
                UnitId: '',
                unit: '',
                qty: 1,
                rate: 0,
                disc: 0,
                tax: 0,
                batchNo: '',
                expDate: '',
                serialNo: '',
                remark: '',
                CGST: '0.00',
                SGST: '0.00',
                IGST: '0.00',
                UTGST: '0.00',
                GSTCess: '0.00',
                TaxId: null,
                challanDtsID: null,
                challanID: null,
                soId: null,
                soDtsID: null
            };
            return { ...l, rows: newRows, nextSrNo: newNextSr, current: resetCurrent, errors: {}, editIndex: null };
        }));
    };

    const editRow = (levelId, index) => {
        const level = levels.find(l => l.id === levelId);
        if (!level) return;
        const row = level.rows[index];
        const matchedUnit = level.unitOptions.find(u => String(u.UnitId) === String(row.UnitId)) ||
            level.unitOptions.find(u => String(u.UnitId) === String(row.unit)) ||
            level.unitOptions.find(u => u.unit?.toLowerCase() === String(row.unit).toLowerCase());
        setLevels(prev => prev.map(l => l.id === levelId ? {
            ...l,
            current: {
                ...row,
                Id: row.productId,
                UnitId: matchedUnit ? String(matchedUnit.UnitId) : row.UnitId || '',
                unit: matchedUnit ? matchedUnit.unit : row.unit || '',
                CGST: String(row.CGST || '0.00'),
                SGST: String(row.SGST || '0.00'),
                IGST: String(row.IGST || '0.00'),
                UTGST: String(row.UTGST || '0.00'),
                GSTCess: String(row.GSTCess || '0.00'),
                TaxId: row.TaxId || null,
                challanDtsID: row.challanDtsID || null,
                challanID: row.challanID || null,
                soId: row.soId || null,
                soDtsID: row.soDtsID || null
            },
            editIndex: index
        } : l));
    };

    const deleteRow = (levelId, index) => {
        setLevels(prev => prev.map(l => l.id === levelId ? { ...l, rows: l.rows.filter((_, i) => i !== index) } : l));
    };

    useEffect(() => {
        const n = v => Number(v || 0);
        let basicAmountSum = 0;
        let discountTotal = 0;
        let cgstTotal = 0;
        let sgstTotal = 0;
        let igstTotal = 0;
        levels.forEach(level => {
            level.rows.forEach(r => {
                const qty = n(r.qty);
                const rate = n(r.rate);
                const taxable = qty * rate;
                const taxPct = n(r.tax);
                const isSameState = state?.toLowerCase() === compState?.toLowerCase();
                const cgstAmtRow = isSameState ? taxable * (taxPct / 100) / 2 : 0;
                const sgstAmtRow = isSameState ? taxable * (taxPct / 100) / 2 : 0;
                const igstAmtRow = isSameState ? 0 : taxable * (taxPct / 100);
                basicAmountSum += taxable;
                discountTotal += 0;
                cgstTotal += cgstAmtRow;
                sgstTotal += sgstAmtRow;
                igstTotal += igstAmtRow;
            });
        });
        const totalTax = cgstTotal + sgstTotal + igstTotal;
        const finalAmount = basicAmountSum + totalTax;
        const roundedFinal = Math.ceil(finalAmount);
        const roundOff = Number((roundedFinal - finalAmount).toFixed(2));
        setTotals({
            basic: basicAmountSum.toFixed(2),
            discount: discountTotal.toFixed(2),
            cgst: cgstTotal.toFixed(2),
            sgst: sgstTotal.toFixed(2),
            igst: igstTotal.toFixed(2),
            roundOff: roundOff.toFixed(2),
            total: roundedFinal.toFixed(2)
        });
    }, [levels, state, compState]);

    const formatINR = (val) => {
        const num = Number(val) || 0;
        return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const columns = [
        { field: 'srNo', headerName: '#', width: 40, align: 'center', headerAlign: 'center' },
        {
            field: 'product', headerName: 'Product', flex: 1, sortable: false, filterable: false,
            renderCell: (params) => {
                const { productCode, description, unit, tax, batchNo, expDate, serialNo, remark, CGST, SGST, IGST, index, levelId } = params.row;
                const isSameState = state?.toLowerCase() === compState?.toLowerCase();
                return (
                    <Box sx={{ py: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box>
                            <Typography variant="body2" fontWeight="bold" sx={{ cursor: 'pointer', color: theme.palette.primary.main, textDecoration: 'underline', fontSize: '0.85rem' }} onClick={(e) => { e.stopPropagation(); editRow(levelId, index); }} >
                                {productCode}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
                                {description}
                            </Typography>
                        </Box>
                        <Typography variant="caption"> Unit: {unit || 'N/A'} | GST: {tax}% </Typography>
                        <Typography variant="caption" color="text.secondary"> {isSameState ? `SGST: ${SGST}% + CGST: ${CGST}%` : `IGST: ${IGST}%`} </Typography>
                        {(batchNo || expDate || serialNo) && (
                            <Typography variant="caption">
                                {batchNo && `Batch: ${batchNo}`}
                                {expDate && ` | Exp: ${expDate}`}
                            </Typography>
                        )}
                        {remark && (
                            <Typography variant="caption" color="text.secondary"> Remark: {remark} </Typography>
                        )}
                    </Box>
                );
            }
        },
        { field: 'qty', headerName: 'BOM Qty', width: 100, align: 'center', headerAlign: 'center' },
        { field: 'rate', headerName: 'Rate', width: 100, align: 'right', headerAlign: 'center', renderCell: params => `₹${formatINR(params.row.rate)}` }
    ];

    const handleSubmit = async () => {
        const companyId = companyData?.[0]?.CompId;
        const username = loggedInUser?.UserId || 123;
        try {
            if (!bomProductCode || !topLevelProduct.Id) {
                setError('Please select a product code');
                return;
            }
            const currentDate = new Date();
            const bomData = {
                GCompId: companyData?.[0]?.GCompId || companyId,
                CompID: companyId,
                category: type,
                ProdId: topLevelProduct.Id || null,
                BomDate: currentDate.toISOString(),
                BranchId: loggedInUser?.details?.BranchId || 1,
                AddUserId: username,
                AddUserDateTime: currentDate.toISOString(),
                FYear: `${currentDate.getFullYear()}-${currentDate.getFullYear() + 1}`,
                details: levels.flatMap(level =>
                    level.rows.map(row => ({
                        category: type,
                        MastProdId: topLevelProduct.Id || null,
                        DtsProdId: row.productId,
                        UnderProdId: topLevelProduct.Id || null,
                        BomQty: Number(row.qty),
                        BranchId: loggedInUser?.details?.BranchId || 1,
                        AddUserId: username,
                        AddUserDateTime: currentDate.toISOString(),
                        FYear: `${currentDate.getFullYear()}-${currentDate.getFullYear() + 1}`
                    }))
                )
            };
            console.log('BOM:', bomData);
            const response = await axios.post(`${connectionString}/api/bom/create`, bomData);
            console.log('BOM Created:', response.data);

            // Clear fields and DataGrid on 201 status
            if (response.status === 201) {
                setBomProductCode('');
                setBomDescription('');
                setTopLevelProduct({
                    Id: '',
                    itemCode: '',
                    description: '',
                    Unit: '',
                    GST: '0.00',
                    saleRate: 0,
                    saleDiscount: 0,
                    TaxId: null
                });
                setProductOptions([]);
                setType('Processing Materials'); // Reset to default
                setLevels([
                    {
                        id: 0,
                        current: {
                            Id: '',
                            productCode: '',
                            description: '',
                            UnitId: '',
                            unit: '',
                            qty: 1,
                            rate: 0,
                            disc: 0,
                            tax: 0,
                            batchNo: '',
                            expDate: '',
                            serialNo: '',
                            remark: '',
                            CGST: '0.00',
                            SGST: '0.00',
                            IGST: '0.00',
                            UTGST: '0.00',
                            GSTCess: '0.00',
                            TaxId: null,
                            challanDtsID: null,
                            challanID: null,
                            soId: null,
                            soDtsID: null
                        },
                        rows: [],
                        nextSrNo: 1,
                        errors: {},
                        editIndex: null,
                        unitOptions: levels[0].unitOptions, // Preserve unitOptions
                        allProducts: []
                    }
                ]);
                setError(null);
            }
        } catch (e) {
            console.error('Error creating BOM:', e);
            setError('Failed to create BOM');
        }
    };

    useEffect(() => {
        loadAllProducts();
    }, [type, connectionString]);

    const formatDateDDMMYYYY = (date) => {
        const d = date.getDate().toString().padStart(2, '0');
        const m = (date.getMonth() + 1).toString().padStart(2, '0'); // months are zero-based
        const y = date.getFullYear();
        return `${d}-${m}-${y}`;
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '1200px', mx: 'auto' }}>
            <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
                Bill of Materials
            </Typography>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Type</InputLabel>
                <Select size="small" value={type} label="Type"
                    onChange={(e) => {
                        const selectedType = e.target.value;
                        setType(selectedType);
                        fetchProducts(selectedType, bomProductCode);
                    }}
                >
                    <MenuItem value="Finished Goods">Finished Goods</MenuItem>
                    <MenuItem value="Assembly">Assembly</MenuItem>
                    <MenuItem value="Sub-Assembly">Sub-Assembly</MenuItem>
                    <MenuItem value="Processing Materials">Processing Materials</MenuItem>
                </Select>
            </FormControl>

            <TextField fullWidth label="Date" value={formatDateDDMMYYYY(new Date())} disabled sx={{ mb: 2 }} size="small" />

            <Autocomplete fullWidth options={productOptions} getOptionLabel={(option) => option.itemCode || ''} value={productOptions.find(p => p.itemCode === bomProductCode) || null} onChange={handleBomProductSelect} renderInput={(params) => (<TextField {...params} label="Product Code" sx={{ mb: 2 }} size="small" />)}
                onInputChange={(_, newValue) => {
                    setBomProductCode(newValue);
                    if (newValue && type) {
                        fetchProducts(type, newValue);
                    } else {
                        setBomDescription('');
                        setProductOptions([]);
                    }
                }}
                renderOption={(props, option) => (
                    <li {...props}>
                        <Box>
                            <Typography variant="body2">{option.itemCode}</Typography>
                            <Typography variant="caption" color="text.secondary">{option.description}</Typography>
                        </Box>
                    </li>
                )}
            />

            <TextField fullWidth label="Description" value={bomDescription} disabled sx={{ mb: 2 }} size="small" />
            {levels.map(level => {
                const { id, current, rows: levelRows, errors, unitOptions, allProducts, editIndex } = level;
                return (
                    <Box key={id} sx={{ mb: 4, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, p: { xs: 2, md: 3 }, backgroundColor: theme.palette.background.paper }} >
                        <Box display="flex" flexDirection={isSmall ? 'column' : 'row'} gap={2} sx={{ width: '100%', flexWrap: isSmall ? 'wrap' : 'nowrap' }} >
                            <Box display="flex" flexDirection="column" gap={2} sx={{ width: '100%', maxWidth: isSmall ? '100%' : 420, p: 2, borderRight: isSmall ? 'none' : `1px solid ${theme.palette.divider}` }} >
                                <Box>
                                    <DropdownProductCode id="productCode" options={allProducts} value={current.productCode} onSelect={(p) => handleSelect(id, p)} onNewProductAdded={loadAllProducts} connectionString={connectionString} />
                                    {!!errors.productCode && (
                                        <Typography color="error" variant="caption">
                                            {errors.productCode}
                                        </Typography>
                                    )}
                                </Box>
                                <TextField label="Description" fullWidth multiline minRows={1} size="small" placeholder="Enter product description" value={current.description} onChange={handleChange(id, 'description')} sx={{ '& .MuiInputBase-root': { minHeight: 40 } }} />
                                <Box display="flex" flexDirection="row" gap={1.5}>
                                    <TextField label="Unit" select fullWidth size="small" value={String(current.UnitId)} onChange={handleChange(id, 'UnitId')} SelectProps={{ native: true }} sx={{ '& .MuiOutlinedInput-root': { height: 40 } }} >
                                        <option value=""></option>
                                        {unitOptions.map(u => (<option key={u.UnitId} value={u.UnitId}>{u.unit}</option>))}
                                    </TextField>
                                    <TextField label="Tax(%)" fullWidth type="number" disabled value={current.tax} onChange={handleChange(id, 'tax')} sx={{ '& .MuiOutlinedInput-root': { height: 40 } }} size="small" />
                                </Box>
                                <Box display="flex" flexDirection="row" gap={1.5}>
                                    <TextField label="Qty" fullWidth type="number" inputProps={{ min: 1 }} value={current.qty} onChange={handleChange(id, 'qty')} error={!!errors.qty} helperText={errors.qty} sx={{ '& .MuiOutlinedInput-root': { height: 40 } }} size="small" />
                                    <TextField label="Rate" fullWidth type="number" inputProps={{ min: 0 }} value={current.rate} onChange={handleChange(id, 'rate')} sx={{ '& .MuiOutlinedInput-root': { height: 40 } }} size="small" />
                                </Box>
                                <TextField label="Remark" fullWidth multiline minRows={1} maxRows={3} size="small" value={current.remark || ''} onChange={handleChange(id, 'remark')} sx={{ '& .MuiInputBase-root': { minHeight: 40 } }} />
                                <Box display="flex" justifyContent="center">
                                    <Button variant="outlined" onClick={() => addProduct(id)} sx={{ minWidth: 180, textTransform: 'none' }} >
                                        {editIndex != null ? 'Update Cart' : 'Add to Cart'}
                                    </Button>
                                </Box>
                            </Box>
                            <Box sx={{ width: '100%', p: isSmall ? 0 : 2, mt: isSmall ? 2 : 0 }}>
                                <DataGrid columns={columns} rows={levelRows.map((r, i) => ({ ...r, id: i + 1, index: i, levelId: id }))} getRowHeight={() => 'auto'} autoHeight disableRowSelectionOnClick hideFooter
                                    sx={{
                                        border: `1px solid ${theme.palette.divider}`, borderRadius: 2,
                                        '& .MuiDataGrid-columnHeaders': { bgcolor: theme.palette.grey[100], minHeight: 36, maxHeight: 36 },
                                        '& .MuiDataGrid-columnHeader': { fontSize: '0.85rem', fontWeight: 'bold' },
                                        '& .MuiDataGrid-row:nth-of-type(even)': { bgcolor: theme.palette.grey[50] },
                                        '& .MuiDataGrid-row:hover': { bgcolor: theme.palette.action.hover }
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                );
            })}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button variant="contained" color="primary" onClick={handleSubmit} size="large" sx={{ px: 4, textTransform: 'none' }} >
                    Generate BOM
                </Button>
            </Box>
        </Box>
    );
};

export default MultiLevelBOMForm;